import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtimePostgresChanges } from './useRealtimePostgresChanges';

// Central source-of-truth for the Google-account-auth-lifecycle UI state.
// Backed by google_accounts.auth_status + last_auth_error_at (see migrations
// 20260726035343_google_account_auth_lifecycle.sql and
// 20260726180000_google_account_scope_missing_status.sql).
//
// Sibling of useMcAccessStatus, one level higher in the hierarchy:
//   useMcAccessStatus  → one Merchant Center lost access (token still valid elsewhere)
//   useGoogleAuthStatus → the whole Google account lost access (user hit "Remove access")
//                         or never granted the Merchant API scope in the first place
//
// Two distinct broken states, deliberately kept apart (Finding R2-3):
//   reauth_required → the grant existed and was taken away
//   scope_missing   → consent completed, but the Merchant API checkbox stayed unticked
// They share a symptom (nothing works) but not a repair instruction, so they must not
// share a banner text.
//
// Consumed by:
//   - GoogleAuthRevokedBanner (persistent top banner, mounted in Layout.tsx)

export interface RevokedGoogleAccount {
  id: string;
  email: string;
  lastAuthErrorAt: Date | null;
}

export interface GoogleAuthStatus {
  /** auth_status = 'reauth_required': the user revoked a grant we used to have. */
  revokedAccounts: RevokedGoogleAccount[];
  hasRevokedAccount: boolean;
  primaryRevokedAccount: RevokedGoogleAccount | null;
  /** auth_status = 'scope_missing': consent ran through without the Shopping scope. */
  scopeMissingAccounts: RevokedGoogleAccount[];
  hasScopeMissingAccount: boolean;
  primaryScopeMissingAccount: RevokedGoogleAccount | null;
  isLoading: boolean;
}

interface GoogleAuthRow {
  id: string;
  email: string;
  auth_status: string | null;
  last_auth_error_at: string | null;
}

export const GOOGLE_AUTH_STATUS_QUERY_KEY = 'google-auth-lifecycle';

export function useGoogleAuthStatus(): GoogleAuthStatus {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;
  const queryKey = [GOOGLE_AUTH_STATUS_QUERY_KEY, userId];

  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!userId,
    queryFn: async (): Promise<GoogleAuthRow[]> => {
      // Stable order: the banner shows exactly one account (revokedAccounts[0]), so an
      // unordered query would let the banner jump between accounts on every refetch
      // when two of them are revoked. Oldest connection first.
      // neq('active') instead of eq('reauth_required'): the lifecycle now has two
      // broken states and this filter matches the partial index
      // idx_google_accounts_reauth_required (WHERE auth_status <> 'active') exactly,
      // so a third state later cannot silently fall out of the query again.
      const { data, error } = await supabase
        .from('google_accounts')
        .select('id, email, auth_status, last_auth_error_at')
        .neq('auth_status', 'active')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('useGoogleAuthStatus: failed to load google account auth state', error);
        return [];
      }
      return (data ?? []) as GoogleAuthRow[];
    },
    staleTime: 30_000,
  });

  // Realtime subscription across the user's accounts. Fires whenever an edge function
  // marks an account reauth_required or a reconnect heals it.
  //
  // Hard requirement: google_accounts must be a member of the publication
  // supabase_realtime, otherwise postgres_changes never delivers anything and this
  // handler is dead code (migration 20260726044017_realtime_lifecycle_tables.sql adds
  // it). That matters more than it looks: staleTime is 30s, refetchOnWindowFocus is
  // off, and the banner lives in Layout, which never remounts during SPA navigation.
  // Without the subscription the state is fetched once at app start and then
  // effectively never again.
  useRealtimePostgresChanges(
    userId ? `google-auth-${userId}` : null,
    userId
      ? { event: 'UPDATE', table: 'google_accounts', filter: `user_id=eq.${userId}` }
      : null,
    () => {
      queryClient.invalidateQueries({ queryKey });
    },
  );

  return useMemo<GoogleAuthStatus>(() => {
    // Order comes from the query above (created_at asc), so the primary accounts are
    // deterministic across refetches.
    const byStatus = (status: string): RevokedGoogleAccount[] =>
      (data ?? [])
        .filter((row) => row.auth_status === status)
        .map((row) => ({
          id: row.id,
          email: row.email,
          lastAuthErrorAt: row.last_auth_error_at ? new Date(row.last_auth_error_at) : null,
        }));

    // Explicit equality on both lists, never "everything that is not active": the query
    // widened to neq('active'), and an unknown future status must not be silently
    // rendered with the wrong repair instructions.
    const revokedAccounts = byStatus('reauth_required');
    const scopeMissingAccounts = byStatus('scope_missing');

    return {
      revokedAccounts,
      hasRevokedAccount: revokedAccounts.length > 0,
      primaryRevokedAccount: revokedAccounts[0] ?? null,
      scopeMissingAccounts,
      hasScopeMissingAccount: scopeMissingAccounts.length > 0,
      primaryScopeMissingAccount: scopeMissingAccounts[0] ?? null,
      isLoading,
    };
  }, [data, isLoading]);
}
